import useGetDashboardInvoiceStats from '@/api/queries/dashboard/useGetDashboardInvoiceStats';
import { useTranslation } from '@/i18n';
import { useAccount } from '@/utils/storeHooks';
import { FC } from 'react';
import { DateRange } from '../utils';
import DashboardSectionEmpty from './DashboardSectionEmpty';
import DashboardSectionError from './DashboardSectionError';
import DashboardSectionSkeleton from './DashboardSectionSkeleton';
import DashboardStatsCard from './DashboardStatsCard';

interface DashboardInvoicesProps {
  dateRange?: DateRange;
}

const DashboardInvoices: FC<DashboardInvoicesProps> = ({ dateRange }) => {
  const account = useAccount();
  const translate = useTranslation('dashboard.invoices');
  const translateErrors = useTranslation('dashboard.errors');
  const { stats, isLoading, isError } = useGetDashboardInvoiceStats({
    dateRange,
  });

  if (isLoading) {
    return <DashboardSectionSkeleton variant="stats-card" />;
  }

  if (isError) {
    return <DashboardSectionError message={translateErrors('invoicesError')} />;
  }

  if (!stats) {
    return (
      <DashboardSectionEmpty
        title={translate('title')}
        message={translate('empty')}
      />
    );
  }

  return (
    <DashboardStatsCard
      title={translate('title')}
      region={account?.region}
      stats={[
        {
          key: 'sent',
          label: translate('sent'),
          color: '#3b82f6', // blue-500
          data: stats.sent,
          tooltip: translate('sentTooltip'),
        },
        {
          key: 'paid',
          label: translate('paid'),
          color: '#22c55e', // green-500
          data: stats.paid,
          showRate: true,
          tooltip: translate('paidTooltip'),
        },
        {
          key: 'due',
          label: translate('overdue'),
          color: '#eab308', // yellow-500
          data: stats.due,
          showRate: true,
          tooltip: translate('overdueTooltip'),
        },
      ]}
      timeSeries={stats.timeSeries}
      chartSeries={[
        {
          name: translate('sent'),
          countKey: 'sentCount',
          valueKey: 'sentValue',
          color: '#3b82f6', // blue-500
        },
        {
          name: translate('paid'),
          countKey: 'paidCount',
          valueKey: 'paidValue',
          color: '#22c55e', // green-500
        },
        {
          name: translate('overdue'),
          countKey: 'dueCount',
          valueKey: 'dueValue',
          color: '#eab308', // yellow-500
        },
      ]}
      rateSeries={{
        name: translate('paidRate'),
        numeratorKey: 'paidCount',
        denominatorKey: 'sentCount',
        color: '#a855f7', // purple-500
      }}
      xAxisTitle={translate('xAxisTitle')}
    />
  );
};

export default DashboardInvoices;
